import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Play } from "lucide-react";
import { http, errMsg } from "../../lib/api";

const LABELS = { two: "Allow 2 pages", fit: "Fit to 1 page", fill: "Fill page", accept: "Accept as is", later: "Leave for now" };

export const planItems = (pc) => {
  const rows = pc?.pages || [];
  const span = {};
  rows.forEach((r) => { if (r.status === "overflow") span[r.block] = (span[r.block] || 0) + 1; });
  const seen = new Set();
  return rows.flatMap((r) => {
    if (seen.has(r.block)) return [];
    if (r.status === "overflow") { seen.add(r.block); return [{ r, kind: "spill", pages: span[r.block], opts: span[r.block] === 2 ? ["two", "fit", "later"] : ["fit", "later"] }]; }
    if (r.status === "light") { seen.add(r.block); return [{ r, kind: "under", opts: r.acceptable ? ["fill", "accept", "later"] : ["fill", "later"] }]; }
    return [];
  });
};

const Item = ({ it, value, set }) => (
  <li data-testid={`plan-item-${it.r.n}`} className="flex flex-wrap items-center gap-3 py-2 border-t border-stone-100">
    <span className="font-mono-ui text-xs text-stone-500 w-10">p.{it.r.n}</span>
    <span className="text-sm text-[#2B2A26] flex-1 min-w-[12rem]">{it.r.label}
      <span className={`ml-2 text-[11px] ${it.kind === "spill" ? "text-red-700" : "text-amber-700"}`}>
        {it.kind === "spill" ? `spills onto ${it.pages} pages` : `${Math.round(it.r.fill * 100)}% full`}
      </span>
    </span>
    <span className="inline-flex rounded-sm border border-stone-300 overflow-hidden">
      {it.opts.map((o) => (
        <button key={o} data-testid={`plan-${it.r.n}-${o}`} onClick={() => set(it.r.block, o)} aria-pressed={value === o}
          className={`px-3 py-1 text-xs border-l first:border-l-0 border-stone-300 transition-colors ${value === o ? "bg-[#2B2A26] text-white" : "bg-white text-stone-700 hover:bg-stone-100"}`}>
          {LABELS[o]}
        </button>
      ))}
    </span>
  </li>
);

export const PagePlan = ({ kit, reload, disabled }) => {
  const items = planItems(kit.page_check);
  const [choice, setChoice] = useState({});
  const [busy, setBusy] = useState(false);
  if (!items.length) return null;
  const answered = items.filter((it) => choice[it.r.block]).length;
  const pick = (o) => items.filter((it) => choice[it.r.block] === o).map((it) => it.r.block);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await http.post(`/kits/${kit.id}/page-plan`, { fill: pick("fill"), fit: pick("fit"), accept: pick("accept"), two: pick("two") });
      toast.success(data.running ? "Working through your choices — accepted and 2-page choices are saved; fills and fits are running now" : "Your choices are saved");
      setChoice({});
      await reload();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const setAll = (o) => setChoice(Object.fromEntries(items.map((it) => [it.r.block, it.opts.includes(o) ? o : choice[it.r.block]]).filter(([, v]) => v)));
  return (
    <div data-testid="page-plan" className="mb-5 rounded-lg border border-amber-200 bg-amber-50/40 p-4">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <b className="text-sm text-[#2B2A26]">Decide each problem page, then run them together</b>
        <span data-testid="plan-answered" className="text-xs text-stone-500">{answered} of {items.length} answered</span>
        <span className="ml-auto inline-flex gap-2 text-[11px]">
          {["fill", "fit", "two", "later"].map((o) => items.some((it) => it.opts.includes(o)) && (
            <button key={o} data-testid={`plan-all-${o}`} onClick={() => setAll(o)} className="underline text-stone-600 hover:text-[#2B2A26]">All: {LABELS[o]}</button>
          ))}
        </span>
      </div>
      <ul>{items.map((it) => <Item key={it.r.block} it={it} value={choice[it.r.block]} set={(b, o) => setChoice((c) => ({ ...c, [b]: o }))} />)}</ul>
      <div className="flex items-center gap-3 mt-3">
        <button data-testid="plan-run" onClick={run} disabled={disabled || busy || kit.filling || answered < items.length}
          className="inline-flex items-center gap-2 rounded-sm bg-[#2B2A26] px-4 py-2 text-sm font-bold text-white hover:bg-black transition-colors disabled:opacity-40">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Run my choices
        </button>
        {answered < items.length && <span className="text-xs text-stone-500">Answer every page first ({items.length - answered} left).</span>}
      </div>
    </div>
  );
};
